import { FileTextIcon } from '@phosphor-icons/react';
import { CanvasOpenButton } from './canvas-open-button';
import { useCanvasOpenHandler } from './canvas-link';
import { canvasNewTabHref } from './canvas-panel';

/**
 * FRONT-2594 — chat card for a successful `file_write` document. Rendered ONLY
 * inside a Canvas context (message-bubble decides); outside it a document write
 * falls through to the generic tool card exactly as before.
 */
export function DocumentCard({
  filename,
  title,
  warnings = [],
  attachedTo = null,
}: {
  filename: string;
  title: string;
  /** The write's save warnings — the generic card shows them in its JSON result, so this one must too. */
  warnings?: string[];
  /** Where the write was auto-filed (a Work container), when it was. */
  attachedTo?: { name: string; kind: string } | null;
}) {
  // FRONT-2744: the title opens the document in the Canvas on a plain click; a
  // modifier click follows the real href to the document's own page in a new tab.
  const openInCanvas = useCanvasOpenHandler(`document:${filename}`);
  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="canvas-document-card">
      <div className="px-3 py-2 flex items-center gap-2 text-xs">
        <FileTextIcon size={14} className="text-muted-foreground shrink-0" />
        <a
          href={canvasNewTabHref({ kind: 'document', id: filename })}
          target="_blank"
          rel="noreferrer noopener"
          onClick={openInCanvas}
          data-testid="canvas-document-title"
          className="font-medium truncate text-foreground hover:text-primary hover:underline"
        >
          {title}
        </a>
        <span className="ml-auto inline-flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wide rounded-full bg-muted text-muted-foreground border border-border px-1.5 py-0.5">
          Document
        </span>
        <CanvasOpenButton itemKey={`document:${filename}`} />
      </div>
      {attachedTo && (
        <p data-testid="canvas-document-attached" className="px-3 pb-2 text-xs text-muted-foreground">
          Filed to {attachedTo.name}
        </p>
      )}
      {warnings.length > 0 && (
        <ul data-testid="canvas-document-warnings" className="px-3 pb-2 space-y-0.5 text-xs text-muted-foreground">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
